import { useNavigate } from "react-router";
import * as ROUTES from "../../constants/routes";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { VOLUME_DISCOUNT } from "../../constants/routes";
import { TradeTermsLayout } from "../../components/tradeTermsAssignment/TradeTermsLayout";
import { VolumeDiscountDetail } from "./VolumeDiscountDetail";
import { SpinnerLegacy } from "../../components";
import { useVolumeDiscounts } from "../../hooks/api/v2/volumeDiscounts/useVolumeDiscounts";
import { useState } from "react";
import { PriceTierDiscountDialogCreate } from "../priceTiers/priceTier/edit/PriceTierDiscountDialog/PriceTierDiscountDialogCreate";
import { Icon } from "../../components";
import { PriceTierDiscountDialogUpdate } from "../priceTiers/priceTier/edit/PriceTierDiscountDialog/PriceTierDiscountDialogUpdate";
import { getDiscountDescription } from "@sellar-io/common";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { PriceListDiscountType } from "@sellar-io/api-v2/types";
import { Button, DataButton } from "@sellar-io/components";

export const VolumeDiscount = () => {
  const navigate = useNavigate();
  const volumeDiscountId = useParamAsNumber(
    VOLUME_DISCOUNT,
    "volumeDiscountId",
  );

  const { data: volumeDiscount, isLoading } = useVolumeDiscounts(
    volumeDiscountId,
    {
      enabled: !!volumeDiscountId,
    },
  );

  const existingConnections =
    volumeDiscount?.PriceTiers.flatMap(
      (pt) => pt.SupplierRetailerConnections,
    ) ?? [];

  const [addVolumeDiscountActive, setAddVolumeDiscountActive] = useState(false);

  return (
    <TradeTermsLayout
      title={volumeDiscount?.name ?? ""}
      onClickLeftIcon={() => navigate(ROUTES.VOLUME_DISCOUNTS)}
      aside={<VolumeDiscountDetail />}
      isNew={false}
      hideAssignButton={true}
      existingConnections={existingConnections}
      type="volume discount"
      additionalAssignmentText={`via ${volumeDiscount?.PriceTiers.length ?? 0} price list(s)`}
    >
      <div className="mx-auto flex max-w-230 items-center gap-2">
        {isLoading ? (
          <SpinnerLegacy />
        ) : (
          <VolumeDiscountContent
            volumeDiscount={volumeDiscount}
            onAddDiscount={() => setAddVolumeDiscountActive(true)}
          />
        )}
      </div>
      <PriceTierDiscountDialogCreate
        volumeDiscountsId={volumeDiscountId}
        active={addVolumeDiscountActive}
        onDismiss={() => setAddVolumeDiscountActive(false)}
      />
    </TradeTermsLayout>
  );
};

export const VolumeDiscountContent = ({
  volumeDiscount,
  onAddDiscount,
}: {
  volumeDiscount?: {
    id: number;
    name: string;
    PriceTierDiscounts?: PriceListDiscountType[];
  };
  onAddDiscount: () => void;
}) => {
  const { business } = useSellarCurrentBusiness();
  const [editDialogActive, setEditDialogActive] = useState(false);
  const [selectedDiscount, setSelectedDiscount] =
    useState<PriceListDiscountType>();

  const currency = business?.Supplier?.currency;

  return (
    <div className="mb-48 flex w-full flex-col gap-4 px-4">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col gap-1">
            <span className="text-lg font-semibold">Volume discount rules</span>
            <span className="text-zinc-500">
              Volume discount rules apply to eligible product prices excluding
              VAT. Only the formats stated here will be affected by the
              discount.
            </span>
          </div>
          <DataButton
            className="flex items-center gap-1"
            border={true}
            onClick={onAddDiscount}
          >
            <Icon icon="PLUS" />
            New
          </DataButton>
        </div>

        <div className="flex flex-col gap-2">
          {volumeDiscount?.PriceTierDiscounts &&
          volumeDiscount.PriceTierDiscounts.length > 0 ? (
            volumeDiscount.PriceTierDiscounts.map((discount) => {
              const discountDescription = getDiscountDescription({
                discount,
                currency,
              });

              return (
                <div
                  key={discount.id}
                  className="flex cursor-pointer items-center justify-between gap-3 overflow-hidden rounded-lg border border-solid bg-white p-3 hover:border-zinc-700"
                  onClick={() => {
                    setSelectedDiscount(discount);
                    setEditDialogActive(true);
                  }}
                >
                  <span>{discountDescription}</span>
                  <Button size="small" color="transparent" className="p-1">
                    <Icon icon="PENCIL" className="text-black" size={0.7} />
                  </Button>
                </div>
              );
            })
          ) : (
            <div className="rounded-lg border border-zinc-300 bg-zinc-50 p-4 text-center text-zinc-500">
              No volume discounts added yet
            </div>
          )}
        </div>
      </div>

      {selectedDiscount && (
        <PriceTierDiscountDialogUpdate
          priceTierDiscount={selectedDiscount}
          active={editDialogActive}
          onDismiss={() => {
            setEditDialogActive(false);
          }}
        />
      )}
    </div>
  );
};
